import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Search, X, Loader, Edit2 } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

// Currency symbol mapping
const getCurrencySymbol = (currencyCode: string): string => {
  const currencyMap: Record<string, string> = {
    'EUR': '€',
    'USD': '$',
    'HUF': 'Ft',
    'GBP': '£',
    'CHF': 'Fr',
    'SEK': 'kr',
    'NOK': 'kr',
    'DKK': 'kr',
    'CZK': 'Kč',
    'PLN': 'zł',
    'RON': 'lei',
    'RSD': 'дин',
    'HRK': 'kn',
  };
  return currencyMap[currencyCode] || currencyCode;
};

// Format quantity: hide trailing ".0" and trim unnecessary zeros
const formatQuantity = (q: any): string => {
  if (q === null || q === undefined) return '';
  const s = String(q).trim();
  if (s === '') return '';
  const num = Number(s);
  if (!isFinite(num)) return s;
  // If integer, show without decimal part
  if (Number.isInteger(num)) return String(num);
  // Otherwise return minimal representation (removes trailing zeros)
  return String(num);
};

// --- Image Modal ---
const ImageModal = ({ isOpen, imageUrl, productName, onClose }: { isOpen: boolean; imageUrl: string; productName: string; onClose: () => void }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative max-w-4xl max-h-[90vh] bg-white dark:bg-slate-900 rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 z-10 transition-colors"
        >
          <X className="h-6 w-6" />
        </button>
        <div className="flex flex-col items-center justify-center h-full">
          <img
            src={imageUrl}
            alt={productName}
            className="w-full h-full object-contain"
          />
          <div className="absolute bottom-4 left-4 right-4 bg-black/70 text-white p-3 rounded-md">
            <p className="text-sm font-medium">{productName}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Product Card ---
const ProductCardV1 = ({ product, onFilterChange, currentPage }: { product: any; onFilterChange: (type: string, id: number) => void; currentPage: number }) => {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const navigate = useNavigate();
  const auth = useAuth();
  const isAdmin = auth?.user?.isAdmin || false;

  // Extract prices from the new discounts array format
  let originalPrice = 0;
  let discountedPrice = 0;
  let appPrice = 0;

  if (product.discounts && Array.isArray(product.discounts)) {
    const originalDiscount = product.discounts.find((d: any) => d.type === 'original');
    const specialDiscount = product.discounts.find((d: any) => d.type === 'special');
    const appPriceDiscount = product.discounts.find((d: any) => d.type === 'other_price' && (d.name?.includes('App') || d.name?.includes('app')));

    originalPrice = originalDiscount?.value || 0;
    discountedPrice = specialDiscount?.value || originalPrice;
    appPrice = appPriceDiscount?.value || 0;
  } else {
    // Fallback for old format
    originalPrice = parseFloat(product.original_price || product.price_amount) || 0;
    discountedPrice = parseFloat(product.base_price || product.price_amount) || originalPrice;
  }

  // If app price exists, make it the main price reference
  let mainPrice = appPrice > 0 ? appPrice : discountedPrice;
  let hasAppPrice = appPrice > 0;

  // Calculate discount percentage (from original to main price)
  const calculatedDiscount = originalPrice > 0 ? ((originalPrice - mainPrice) / originalPrice) * 100 : 0;

  // Calculate discount for the non-app/non-main prices
  const appDiscount = appPrice > 0 && discountedPrice > 0 ? Math.round(((appPrice - discountedPrice) / appPrice) * 100) : 0;
  const discountedDiscount = discountedPrice > 0 && originalPrice > 0 ? Math.round(((originalPrice - discountedPrice) / originalPrice) * 100) : 0;

  return (
    <>
      <Card className="flex flex-col h-full overflow-hidden transition-all duration-300 glass-panel border border-white/10 bg-white/[0.05] hover:bg-white/[0.08] hover:shadow-2xl shadow-xl rounded-[32px]">
        <div className="relative h-72 bg-black/20 group cursor-pointer" onClick={() => setIsImageModalOpen(true)}>
          <img
            src={product.slice_url}
            alt={product.product_name}
            className="w-full h-full object-cover object-bottom hover:opacity-90 transition-opacity"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <div className="text-white text-2xl font-bold">🔍 Nagyítás</div>
          </div>

          {/* Discount Badge */}
          {calculatedDiscount > 0 && (
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              <span className="bg-green-500 text-white text-base font-black px-2 py-1 rounded-full shadow-lg border-2 border-white">
                -{Math.round(calculatedDiscount)}%
              </span>
            </div>
          )}
        </div>

        <CardHeader className="pt-6">
          {product.brand_name && (
            <div className="text-sm font-black text-purple-300 mb-2">
              {product.brand_name}
            </div>
          )}
          <CardTitle className="break-words text-xl font-black text-white">
            {product.group_members && product.group_members.length > 1
              ? (
                <div className="flex flex-col gap-1">
                  <span>{product.group_members.map((m: any) => m.product_name).join(', ')}</span>

                </div>
              )
              : product.product_name
            }
          </CardTitle>

          {/* Variant info */}
          {/* Variant info - show all packagings if family */}
          {(() => {
            // Try packagings first, fallback to group_members
            const sizeData = (product.packagings && product.packagings.length > 1)
              ? product.packagings.map((pkg: any) => ({
                value: parseFloat(pkg.size_value) || 0,
                display: `${pkg.size_value} ${pkg.size_unit}`
              }))
              : (product.group_members && product.group_members.length > 1)
                ? product.group_members
                  .filter((m: any) => m.variant_quantity && m.variant_unit)
                  .map((m: any) => ({
                    value: parseFloat(m.variant_quantity) || 0,
                    display: `${m.variant_quantity} ${m.variant_unit}`
                  }))
                : [];

            if (sizeData.length > 1) {
              // Get unique sizes
              const uniqueSizes = sizeData.filter((s: any, idx: number, arr: any[]) => arr.findIndex((a: any) => a.display === s.display) === idx);

              if (uniqueSizes.length > 1) {
                const minSize = uniqueSizes.reduce((prev: any, curr: any) => curr.value < prev.value ? curr : prev);
                const maxSize = uniqueSizes.reduce((prev: any, curr: any) => curr.value > prev.value ? curr : prev);

                return (
                  <div className="text-sm text-white/60 font-medium mt-2">
                    📦 {minSize.display} - {maxSize.display}
                  </div>
                );
              } else if (uniqueSizes.length === 1) {
                return (
                  <div className="text-sm text-white/60 font-medium mt-2">
                    📦 {uniqueSizes[0].display}
                  </div>
                );
              }
            }

            // Fallback for single product
            if (product.variant_quantity || product.variant_unit) {
              return (
                <div className="text-sm text-white/60 font-medium mt-2">
                  📦 {formatQuantity(product.variant_quantity)} {product.variant_unit}
                </div>
              );
            }

            return null;
          })()}

          <div className="flex flex-col gap-3 pt-3">
            <div className="flex items-center gap-1 flex-wrap">
              <Badge className="text-xs bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-all">{product.shop_name || "N/A"}</Badge>
            </div>

            {/* 3-szintű kategória hierarchia */}
            <div className="flex flex-col gap-1 mt-2">
              {/* Nagykategória (grandparent) */}
              {product.grandparent_category_id && product.grandparent_category_name && (
                <button
                  onClick={() => onFilterChange("grandparent_category", product.grandparent_category_id)}
                  className="hover:opacity-70 transition-opacity text-left"
                >
                  <Badge className="text-xs cursor-pointer bg-green-500/20 text-green-300 border border-green-500/30 hover:bg-green-500/30 transition-all">{product.grandparent_category_name}</Badge>
                </button>
              )}

              {/* Szülőkategória (parent) */}
              {product.parent_category_id && product.parent_category_name && (
                <button
                  onClick={() => onFilterChange("parent_category", product.parent_category_id)}
                  className="hover:opacity-70 transition-opacity text-left"
                >
                  <Badge className="text-xs cursor-pointer bg-orange-500/20 text-orange-300 border border-orange-500/30 hover:bg-orange-500/30 transition-all">{product.parent_category_name}</Badge>
                </button>
              )}

              {/* Végső kategória (category) */}
              {product.category_id && product.category_name && (
                <button
                  onClick={() => onFilterChange("category", product.category_id)}
                  className="hover:opacity-70 transition-opacity text-left"
                >
                  <Badge className="text-xs bg-blue-500/20 text-blue-300 border border-blue-500/30 cursor-pointer hover:bg-blue-500/30 transition-all">{product.category_name}</Badge>
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-grow bg-white/[0.02]">
          <div className="space-y-4">

            <div className="relative">
              {/* Minimum quantity badge - positioned above the price box */}
              {product.min_quantity && product.min_quantity > 1 && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                  <div className="flex items-center gap-1 text-xs font-black text-white bg-slate-700 border border-slate-600 rounded-lg px-3 py-1 whitespace-nowrap">
                    <span>📦</span>
                    <span>Min. vásárlás: {product.min_quantity} db</span>
                  </div>
                </div>
              )}

              {/* Price box - APP price is main if available */}
              <div className="border-2 border-white/20 rounded-lg p-4 bg-white/5">
                {/* Main price display (either APP or discounted) */}
                <div className="flex flex-col items-center mb-4 pb-4 border-b border-white/10">
                  {calculatedDiscount > 0 && (
                    <div className="flex gap-1 mb-2">
                      <div className="bg-green-500 px-2 py-1 rounded font-black text-white text-xl">
                        -{Math.round(calculatedDiscount)}%
                      </div>
                    </div>
                  )}
                  <span className="text-3xl font-black text-white">{mainPrice.toFixed(2)} {product.currency_symbol || getCurrencySymbol(product.price_currency)}</span>
                  {hasAppPrice && (
                    <span className="text-xs font-black text-purple-300 mt-2">📱 APP ÁR</span>
                  )}
                </div>

                {/* Prices comparison box */}
                {(hasAppPrice || discountedPrice > 0 || originalPrice > 0) && (
                  <div className="bg-white/5 rounded-lg p-3 mb-3">
                    <div className="text-xs font-black text-white/60 mb-2">ÁRAK ÖSSZEHASONLÍTÁSA:</div>

                    {/* APP price row */}
                    {hasAppPrice && (
                      <div className="flex justify-between items-center mb-2 pb-2 border-b border-white/10">
                        <div>
                          <div className="text-xs text-purple-300 font-black">📱 APP ÁR:</div>
                          <div className="text-sm text-purple-200 font-black">{appPrice.toFixed(2)} {product.currency_symbol || getCurrencySymbol(product.price_currency)}</div>
                        </div>
                        {appDiscount !== 0 && (
                          <div className="text-right">
                            <div className="text-xs text-purple-400 font-black">
                              {appDiscount > 0 ? `-${appDiscount}%` : `+${Math.abs(appDiscount)}%`}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Discounted price row */}
                    {discountedPrice > 0 && !hasAppPrice && (
                      <div className="flex justify-between items-center mb-2 pb-2 border-b border-white/10">
                        <div>
                          <div className="text-xs text-green-300 font-black">KEDVEZMÉNYES ÁR:</div>
                          <div className="text-sm text-green-200 font-black">{discountedPrice.toFixed(2)} {product.currency_symbol || getCurrencySymbol(product.price_currency)}</div>
                        </div>
                        {discountedDiscount !== 0 && (
                          <div className="text-right">
                            <div className="text-xs text-green-400 font-black">
                              -{discountedDiscount}%
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Original price row */}
                    {originalPrice > 0 && originalPrice !== mainPrice && (
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-xs text-white/50 font-black">EREDETI ÁR:</div>
                          <div className="text-sm text-white/40 line-through font-black">{originalPrice.toFixed(2)} {product.currency_symbol || getCurrencySymbol(product.price_currency)}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {product.deposit && (
                  <div className="text-center mt-2 text-xs font-black text-blue-300 bg-blue-500/20 rounded px-2 py-1">
                    + betétdíj: {product.deposit.price} {product.currency_symbol || getCurrencySymbol(product.price_currency)}
                  </div>
                )}

                {/* Unit price display */}
                {(() => {
                  const currencySymbol = product.currency_symbol || getCurrencySymbol(product.price_currency);
                  const quantity = parseFloat(product.variant_quantity || '0') || 0;
                  const rawUnit = (product.variant_unit || '').toLowerCase();
                  const unit = rawUnit.replace(/\s+/g, '');

                  const isKg = unit === 'kg' || unit.includes('kilogram');
                  const isG = unit === 'g' || unit.includes('gram');
                  const isMl = unit.includes('ml') || unit.includes('millil');
                  const isCl = unit.includes('cl');
                  const isDl = unit.includes('dl');
                  const isL = (unit === 'l' || unit.includes('liter') || unit.includes('litre') || unit.includes('ltr')) && !isMl && !isCl && !isDl;
                  const isDb = unit === 'db' || unit === 'darab' || unit.includes('piece');

                  let unitPriceDisplay = null;
                  if (quantity > 0 && mainPrice > 0) {
                    if (isKg) {
                      const pricePerKg = mainPrice / quantity;
                      unitPriceDisplay = `${pricePerKg.toFixed(2)} ${currencySymbol}/kg`;
                    } else if (isG) {
                      const pricePer100g = (mainPrice / quantity) * 100;
                      unitPriceDisplay = `${pricePer100g.toFixed(2)} ${currencySymbol}/100g`;
                    } else if (isMl) {
                      const pricePer100ml = (mainPrice / quantity) * 100;
                      unitPriceDisplay = `${pricePer100ml.toFixed(2)} ${currencySymbol}/100ml`;
                    } else if (isCl) {
                      const pricePerCl = mainPrice / quantity;
                      unitPriceDisplay = `${pricePerCl.toFixed(2)} ${currencySymbol}/cl`;
                    } else if (isDl) {
                      const pricePerDl = mainPrice / quantity;
                      unitPriceDisplay = `${pricePerDl.toFixed(2)} ${currencySymbol}/dl`;
                    } else if (isL) {
                      const pricePerL = mainPrice / quantity;
                      unitPriceDisplay = `${pricePerL.toFixed(2)} ${currencySymbol}/l`;
                    } else if (isDb) {
                      const pricePerDb = mainPrice / quantity;
                      unitPriceDisplay = `${pricePerDb.toFixed(2)} ${currencySymbol}/db`;
                    }
                  }

                  return unitPriceDisplay ? (
                    <div className="text-center mt-3 text-xs font-black text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded px-2 py-2">
                      💧 {unitPriceDisplay}
                    </div>
                  ) : null;
                })()}
              </div>
            </div>
            {calculatedDiscount > 0 && (
              <div className="bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/30 rounded-2xl p-3">
                <p className="text-sm font-black text-red-200">
                  💚 Megtakarítás: {((originalPrice - discountedPrice) * (product.min_quantity || 1)).toFixed(2)} {product.currency_symbol || getCurrencySymbol(product.price_currency)}
                </p>
                {product.group_members && product.group_members.length > 1 && (() => {
                  const groupSavings = product.group_members.map((m: any) => {
                    const orig = m.original_price || m.price;
                    const disc = m.discounted_price || orig;
                    return ((orig - disc) * (product.min_quantity || 1)).toFixed(2);
                  });
                  const uniqueSavings = [...new Set(groupSavings)];
                  const mainSaving = ((originalPrice - discountedPrice) * (product.min_quantity || 1)).toFixed(2);
                  const otherSavings = uniqueSavings.filter(s => s !== mainSaving);
                  return otherSavings.length > 0 ? (
                    <p className="text-xs text-red-200/70 mt-1">
                      Vagy: {otherSavings.join(', ')} {product.currency_symbol || getCurrencySymbol(product.price_currency)}
                    </p>
                  ) : null;
                })()}
                {product.min_quantity && product.min_quantity > 1 && (
                  <p className="text-xs font-normal text-red-300 mt-1">({product.min_quantity} db vásárlása esetén)</p>
                )}
                {(product.min_quantity || 1) > 1 && (
                  <p className="text-sm font-black text-green-200 mt-2">
                    💰 {product.min_quantity}x esetén fizetendő: {(discountedPrice * product.min_quantity + (product.deposit ? product.deposit.price * product.min_quantity : 0)).toFixed(2)} {product.currency_symbol || getCurrencySymbol(product.price_currency)}
                  </p>
                )}
              </div>
            )}

            {/* Deposit info box */}
            {product.deposit && (
              <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30 rounded-2xl p-3 mt-3">
                <p className="text-sm font-black text-blue-200">
                  📦 Betétdíj: {product.deposit.label}
                </p>
                <p className="text-xs font-normal text-blue-300 mt-1">
                  Ár darabonként: {product.deposit.price} {product.currency_symbol || getCurrencySymbol(product.price_currency)}
                </p>
                <p className="text-xs font-normal text-blue-300 mt-1">
                  Fizetendő betétdíj: {(product.deposit.price * (product.min_quantity || 1)).toFixed(2)} {product.currency_symbol || getCurrencySymbol(product.price_currency)} ({product.min_quantity || 1} db × {product.deposit.price})
                </p>
                <p className="text-xs text-blue-400/70 mt-2">
                  ♻️ {product.deposit.label} - visszaváltható
                </p>
              </div>
            )}


          </div>
        </CardContent>
        <CardFooter className="flex flex-col items-start gap-3 bg-white/[0.03] p-5 text-xs border-t border-white/5">
          {product.discount_valid_from && (
            <div className="flex items-center text-xs text-white/60 gap-2 w-full">
              <Calendar className="h-4 w-4" />
              <span>{product.discount_valid_from} - {product.discount_valid_to}</span>
            </div>
          )}

          {/* Category Promotions */}
          {product.category_promotions && product.category_promotions.length > 0 && (
            <div className="w-full border-t border-white/5 pt-3 mt-3">
              <div style={{ fontSize: 14, marginBottom: 12, color: '#86efac', fontWeight: 700 }}>
                🎯 KATEGÓRIA SZINTŰ KEDVEZMÉNYEK
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {product.category_promotions.map((promo: any) => {
                  const now = new Date();
                  const endsAt = promo.ends_at ? new Date(promo.ends_at) : null;
                  const isExpired = endsAt && endsAt < now;

                  // Calculate final price after applying category promotion
                  let finalPrice = discountedPrice;
                  if (!isExpired && promo.promotion_type === 'percent') {
                    finalPrice = discountedPrice * (1 - promo.value / 100);
                  } else if (!isExpired && promo.promotion_type === 'fixed') {
                    finalPrice = discountedPrice - promo.value;
                  }
                  finalPrice = Math.max(0, finalPrice); // Ensure no negative prices

                  return (
                    <div key={promo.id} style={{
                      padding: 16,
                      backgroundColor: isExpired ? 'rgba(100, 116, 139, 0.12)' : 'rgba(34, 197, 94, 0.12)',
                      border: isExpired ? '1px solid rgba(100, 116, 139, 0.3)' : '1px solid rgba(34, 197, 94, 0.3)',
                      borderRadius: 8,
                      fontSize: 12,
                      lineHeight: '1.6',
                      opacity: isExpired ? 0.8 : 1
                    }}>
                      <div style={{ color: isExpired ? '#94a3b8' : '#86efac', fontWeight: 700, marginBottom: 8, fontSize: 13 }}>
                        {promo.category_name}
                        {isExpired && <span style={{ marginLeft: 10, color: '#888', fontSize: 12 }}>⏱️ Lejárt</span>}
                      </div>
                      <div style={{ color: isExpired ? '#999' : '#bbb', fontSize: 12, marginBottom: 6 }}>
                        <div style={{ fontWeight: 600 }}>{promo.name}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: isExpired ? '#a0a8b8' : '#a8f5a8', marginTop: 6 }}>
                          {promo.promotion_type === 'percent' ? `${promo.value}%` : `${promo.value} HUF`} kedvezmény
                        </div>
                      </div>

                      {/* Final price calculation display */}
                      {!isExpired && (
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#fbbf24', marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(251, 191, 36, 0.2)' }}>
                          💰 Kedvezményes ár: {finalPrice.toFixed(2)} {product.currency_symbol || getCurrencySymbol(product.price_currency)}
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#d4af37', marginTop: 4 }}>
                            ({discountedPrice.toFixed(2)} Ft - {promo.promotion_type === 'percent' ? `${promo.value}% = ${(discountedPrice - finalPrice).toFixed(2)} Ft` : `${promo.value} Ft = ${(discountedPrice - finalPrice).toFixed(2)} Ft`})
                          </div>
                        </div>
                      )}

                      {promo.starts_at && promo.ends_at && (
                        <div style={{ color: isExpired ? '#777' : '#999', fontSize: 11, marginTop: 8, paddingTop: 8, borderTop: isExpired ? '1px solid rgba(100, 116, 139, 0.2)' : '1px solid rgba(34, 197, 94, 0.2)' }}>
                          📅 {new Date(promo.starts_at).toLocaleDateString('hu-HU')} - {new Date(promo.ends_at).toLocaleDateString('hu-HU')}
                          {isExpired && <span style={{ marginLeft: 8, color: '#777' }}>❌ (lejárt)</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Offer Group Promotions */}
          {product.offer_group_promotions && product.offer_group_promotions.length > 0 && (
            <div className="w-full border-t border-white/5 pt-3 mt-3">
              <div style={{ fontSize: 14, marginBottom: 12, color: '#ffffff', fontWeight: 700 }}>
                🎁 CSOPORT SZINTŰ KEDVEZMÉNYEK
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {product.offer_group_promotions.map((promo: any) => {
                  const now = new Date();
                  const startsAt = promo.starts_at ? new Date(promo.starts_at) : null;
                  const endsAt = promo.ends_at ? new Date(promo.ends_at) : null;
                  const isExpired = endsAt && endsAt < now;
                  const isNotYetActive = startsAt && startsAt > now;

                  // Calculate final price after applying group promotion
                  let finalPrice = discountedPrice;
                  if (!isExpired && promo.discount_percentage) {
                    finalPrice = discountedPrice * (1 - promo.discount_percentage / 100);
                  }
                  finalPrice = Math.max(0, finalPrice); // Ensure no negative prices

                  // DEBUG
                  console.log('OfferGroup Calc:', { discountedPrice, promoPercentage: promo.discount_percentage, finalPrice, isNotYetActive });

                  // Calculate bulk prices based on min_quantity
                  const minQty = product.min_quantity || 1;
                  const totalOriginalPrice = originalPrice * minQty;
                  const totalFinalPrice = finalPrice * minQty;
                  const totalSavings = totalOriginalPrice - totalFinalPrice;
                  const discountFromOriginal = totalOriginalPrice > 0
                    ? ((totalSavings / totalOriginalPrice) * 100).toFixed(2)
                    : 0;

                  return (
                    <div key={promo.id} style={{
                      padding: 16,
                      backgroundColor: isExpired || isNotYetActive ? 'rgba(120, 113, 169, 0.12)' : 'rgba(244, 114, 182, 0.12)',
                      border: isExpired || isNotYetActive ? '1px solid rgba(120, 113, 169, 0.3)' : '1px solid rgba(244, 114, 182, 0.3)',
                      borderRadius: 8,
                      fontSize: 12,
                      lineHeight: '1.6',
                      opacity: isExpired || isNotYetActive ? 0.8 : 1
                    }}>
                      <div style={{ color: isExpired || isNotYetActive ? '#999999' : '#ffffff', fontWeight: 700, marginBottom: 8, fontSize: 13 }}>
                        {promo.name}
                        {isExpired && <span style={{ marginLeft: 10, color: '#888', fontSize: 12 }}>⏱️ Lejárt</span>}
                        {isNotYetActive && <span style={{ marginLeft: 10, color: '#888', fontSize: 12 }}>⏳ Még nem aktív</span>}
                      </div>
                      <div style={{ color: isExpired || isNotYetActive ? '#999' : '#ffffff', fontSize: 12, marginBottom: 6 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: isExpired || isNotYetActive ? '#999999' : '#ffffff', marginTop: 6 }}>
                          {promo.discount_percentage}% kedvezmény
                        </div>
                      </div>

                      {/* Minimum quantity info */}
                      {product.min_quantity && product.min_quantity > 1 && (
                        <div style={{
                          fontSize: 12,
                          color: '#fbbf24',
                          marginTop: 8,
                          paddingTop: 8,
                          borderTop: '1px solid rgba(251, 191, 36, 0.2)',
                          fontWeight: 600
                        }}>
                          📦 Minimális vásárlás: <span style={{ fontWeight: 700 }}>{product.min_quantity} db</span>
                        </div>
                      )}

                      {/* Final price calculation display */}
                      {!isExpired && (
                        <div style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: '#ec4899',
                          marginTop: 10,
                          paddingTop: 10,
                          borderTop: '1px solid rgba(236, 72, 153, 0.2)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 6,
                          opacity: isNotYetActive ? 0.7 : 1
                        }}>
                          {/* Per-unit pricing */}
                          <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#ffffff' }}>
                              💰 Termékenként: {finalPrice.toFixed(2)} {product.currency_symbol || getCurrencySymbol(product.price_currency)}
                            </div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#ffffff', marginTop: 2 }}>
                              ({discountedPrice.toFixed(2)} {product.currency_symbol || getCurrencySymbol(product.price_currency)} - {promo.discount_percentage}% = {finalPrice.toFixed(2)} {product.currency_symbol || getCurrencySymbol(product.price_currency)}, kedvezmény: -{(discountedPrice - finalPrice).toFixed(2)} {product.currency_symbol || getCurrencySymbol(product.price_currency)})
                            </div>
                          </div>

                          {/* Quick summary for bulk purchase */}
                          {product.min_quantity && product.min_quantity > 1 && (
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 3,
                              paddingTop: 6,
                              borderTop: '1px solid rgba(236, 72, 153, 0.2)'
                            }}>
                              <div style={{ fontSize: 11, color: '#10b981', fontWeight: 700 }}>
                                💚 Megtakarítás: {(product.min_quantity * (discountedPrice - finalPrice)).toFixed(2)} {product.currency_symbol || getCurrencySymbol(product.price_currency)}
                              </div>
                              <div style={{ fontSize: 11, color: '#999' }}>
                                ({product.min_quantity} db vásárlása esetén)
                              </div>
                              <div style={{ fontSize: 16, fontWeight: 700, color: '#ffffff' }}>
                                💰 {product.min_quantity}x esetén fizetendő: {(totalFinalPrice + (product.deposit?.price || 0) * product.min_quantity).toFixed(2)} {product.currency_symbol || getCurrencySymbol(product.price_currency)}
                              </div>
                            </div>
                          )}

                          {/* Bulk pricing summary */}
                          {product.min_quantity && product.min_quantity > 1 && (
                            <div style={{
                              paddingTop: 10,
                              borderTop: '1px solid rgba(236, 72, 153, 0.2)',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 5
                            }}>
                              <div style={{ fontSize: 12, color: '#fbbf24' }}>
                                🛒 <span style={{ fontWeight: 700 }}>{product.min_quantity} db vásárlása esetén:</span>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                <div>
                                  <div style={{ fontSize: 10, color: '#999', marginBottom: 2 }}>Eredeti összes ár:</div>
                                  <div style={{ fontSize: 12, color: '#888', textDecoration: 'line-through' }}>
                                    {totalOriginalPrice.toFixed(2)} {product.currency_symbol || getCurrencySymbol(product.price_currency)}
                                  </div>
                                </div>
                                <div>
                                  <div style={{ fontSize: 10, color: '#999', marginBottom: 2 }}>Fizetendő:</div>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: '#22c55e' }}>
                                    {totalFinalPrice.toFixed(2)} {product.currency_symbol || getCurrencySymbol(product.price_currency)}
                                  </div>
                                </div>
                              </div>

                              {/* Deposit calculation if available */}
                              {product.deposit && product.deposit.price > 0 && (
                                <div style={{
                                  fontSize: 11,
                                  color: '#60a5fa',
                                  paddingTop: 6,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}>
                                  <span>+ betétdíj ({product.min_quantity} × {product.deposit.price} {product.deposit.currency}):</span>
                                  <span style={{ fontWeight: 700 }}>{(product.deposit.price * product.min_quantity).toFixed(2)} {product.deposit.currency}</span>
                                </div>
                              )}

                              {/* Total with deposit */}
                              {product.deposit && product.deposit.price > 0 && (
                                <div style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: '#fbbf24',
                                  paddingTop: 8,
                                  borderTop: '1px solid rgba(251, 191, 36, 0.2)',
                                  display: 'flex',
                                  justifyContent: 'space-between'
                                }}>
                                  <span>💰 Összesen:</span>
                                  <span>{(totalFinalPrice + (product.deposit.price * product.min_quantity)).toFixed(2)} {product.currency_symbol || getCurrencySymbol(product.price_currency)}</span>
                                </div>
                              )}

                              <div style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: '#10b981',
                                paddingTop: 8,
                                borderTop: '1px solid rgba(16, 185, 129, 0.2)',
                                display: 'flex',
                                justifyContent: 'space-between'
                              }}>
                                <span>💚 Kedvezmény összesen:</span>
                                <span>{totalSavings.toFixed(2)} {product.currency_symbol || getCurrencySymbol(product.price_currency)} ({discountFromOriginal}%)</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )
                      }

                      {
                        promo.starts_at && promo.ends_at && (
                          <div style={{ color: isExpired || isNotYetActive ? '#777' : '#999', fontSize: 11, marginTop: 8, paddingTop: 8, borderTop: isExpired || isNotYetActive ? '1px solid rgba(120, 113, 169, 0.2)' : '1px solid rgba(244, 114, 182, 0.2)' }}>
                            📅 {new Date(promo.starts_at).toLocaleDateString('hu-HU')} - {new Date(promo.ends_at).toLocaleDateString('hu-HU')}
                            {isExpired && <span style={{ marginLeft: 8, color: '#777' }}>❌ (lejárt)</span>}
                            {isNotYetActive && <span style={{ marginLeft: 8, color: '#777' }}>⏳ (még nem aktív)</span>}
                          </div>
                        )
                      }
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Attributes */}
          {product.attributes && (
            (() => {
              // Check if attributes is an array (new format) or object (old format)
              const isArray = Array.isArray(product.attributes);
              const hasAttributes = isArray
                ? product.attributes.length > 0
                : Object.entries(product.attributes).length > 0;

              if (!hasAttributes) return null;

              return (
                <div className="w-full border-t border-white/5 pt-3 mt-3">
                  {isArray ? (
                    // New format: Array of attribute objects
                    <div className="flex items-center gap-2 flex-wrap">
                      {product.attributes.map((attr: any) => (
                        <span
                          key={attr.id}
                          className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs font-medium border border-blue-500/30"
                        >
                          {attr.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    // Old format: Object with key-value pairs
                    (() => {
                      const flags: string[] = [];
                      const normalAttrs: [string, any][] = [];

                      Object.entries(product.attributes).forEach(([key, value]: [string, any]) => {
                        // Skip internal custom variant fields
                        if (key.startsWith('__custom_variant')) {
                          return;
                        }

                        if (value === null) {
                          flags.push(key);
                        } else {
                          normalAttrs.push([key, value]);
                        }
                      });

                      return (
                        <>
                          {/* Display flags in one row */}
                          {flags.length > 0 && (
                            <div className="flex items-center gap-1 flex-wrap mb-2">
                              {flags.map(flag => (
                                <span key={flag} className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs font-medium border border-blue-500/30">🚩 {flag}</span>
                              ))}
                            </div>
                          )}

                          {/* Display normal attributes */}
                          <div className="space-y-1">
                            {normalAttrs.map(([key, value]) => (
                              <div key={key} className="flex justify-between gap-2 text-white/60 text-xs">
                                <span className="font-medium">{key}:</span>
                                <span>{String(value)}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()
                  )}
                  {!isArray && product.attributes['__custom_variant_quantity'] && (
                    <div className="flex justify-between gap-2 font-black text-green-300 border-t border-white/5 pt-2 mt-2">
                      <span>📦 Kiszerelés:</span>
                      <span>{formatQuantity(product.attributes['__custom_variant_quantity'])} {product.attributes['__custom_variant_unit_name']}</span>
                    </div>
                  )}
                </div>
              );
            })()
          )}

          {/* Unit Price Calculator */}
          {(product.variant_quantity || (product.attributes && product.attributes['__custom_variant_quantity'])) && originalPrice > 0 && (
            <div className="w-full bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-2xl p-3 mt-3 space-y-2">
              <div className="font-black text-yellow-200 text-sm">📊 Egységár</div>

              {(() => {
                const currencySymbol = product.currency_symbol || getCurrencySymbol(product.price_currency || 'EUR');
                const results: JSX.Element[] = [];

                // If packagings have price_per_unit info from API, show ALL packagings
                if (product.packagings && product.packagings.length > 0) {
                  // Get unique packaging sizes to avoid duplicates
                  const uniqueSizeMap = new Map();
                  product.packagings.forEach((pkg: any) => {
                    const sizeKey = `${pkg.size_value}${pkg.size_unit}`;
                    if (!uniqueSizeMap.has(sizeKey)) {
                      uniqueSizeMap.set(sizeKey, pkg);
                    }
                  });

                  uniqueSizeMap.forEach((pkg: any) => {
                    if (pkg.price_per_unit) {
                      results.push(
                        <div key={`original-per-unit-${pkg.size_value}${pkg.size_unit}`} className="flex justify-between text-sm">
                          <span className="text-yellow-300">💪 {pkg.price_per_unit.label} ({pkg.size_value} {pkg.size_unit}):</span>
                          <span className="font-black text-yellow-200">{parseFloat(pkg.price_per_unit.value).toFixed(2)} {currencySymbol}{pkg.price_per_unit.unit}</span>
                        </div>
                      );
                    }
                  });

                  if (results.length > 0) {
                    return results;
                  }
                }

                // Fallback: calculate from variant_quantity if API data not available
                const quantity = parseFloat(product.variant_quantity || product.attributes?.['__custom_variant_quantity']) || 0;
                const rawUnit = (product.variant_unit || product.attributes?.['__custom_variant_unit_name'] || '').toLowerCase();

                // Normalize unit name to catch variants like "milliliter", "millilitre", "ml", etc.
                const unit = rawUnit.replace(/\s+/g, '');
                const isKg = unit.includes('kg') || unit.includes('kilogram');
                const isG = unit === 'g' || unit.includes('gram');
                const isMl = unit.includes('ml') || unit.includes('millil');
                const isCl = unit.includes('cl');
                const isDl = unit.includes('dl');
                const isL = (unit === 'l' || unit.includes('liter') || unit.includes('litre') || unit.includes('ltr')) && !isMl && !isCl && !isDl;
                const isPiece = unit.includes('darab') || unit.includes('db') || unit.includes('pcs') || unit.includes('piece');

                // /KG and /G calculations
                if (isKg || isG) {
                  if (isKg) {
                    const pricePerKg = originalPrice / quantity;
                    results.push(
                      <div key="kg" className="flex justify-between text-sm">
                        <span className="text-yellow-300">💪 Ár/kg:</span>
                        <span className="font-black text-yellow-200">{pricePerKg.toFixed(2)} {currencySymbol}/kg</span>
                      </div>
                    );
                  } else {
                    const pricePer100g = (originalPrice / quantity) * 100;
                    results.push(
                      <div key="100g" className="flex justify-between text-sm">
                        <span className="text-yellow-300">💪 Ár/100g:</span>
                        <span className="font-black text-yellow-200">{pricePer100g.toFixed(2)} {currencySymbol}/100g</span>
                      </div>
                    );
                  }
                }

                // /L calculations (ml, cl, dl, l)
                if (isMl) {
                  const pricePer100ml = (originalPrice / quantity) * 100;
                  results.push(
                    <div key="100ml" className="flex justify-between text-sm">
                      <span className="text-yellow-300">💧 Ár/100ml:</span>
                      <span className="font-black text-yellow-200">{pricePer100ml.toFixed(2)} {currencySymbol}/100ml</span>
                    </div>
                  );
                } else if (isCl) {
                  const pricePerCl = originalPrice / quantity;
                  results.push(
                    <div key="cl" className="flex justify-between text-sm">
                      <span className="text-yellow-300">💧 Ár/cl:</span>
                      <span className="font-black text-yellow-200">{pricePerCl.toFixed(2)} {currencySymbol}/cl</span>
                    </div>
                  );
                } else if (isDl) {
                  const pricePerDl = originalPrice / quantity;
                  results.push(
                    <div key="dl" className="flex justify-between text-sm">
                      <span className="text-yellow-300">💧 Ár/dl:</span>
                      <span className="font-black text-yellow-200">{pricePerDl.toFixed(2)} {currencySymbol}/dl</span>
                    </div>
                  );
                } else if (isL) {
                  const pricePerL = originalPrice / quantity;
                  results.push(
                    <div key="l" className="flex justify-between text-sm">
                      <span className="text-yellow-300">💧 Ár/l:</span>
                      <span className="font-black text-yellow-200">{pricePerL.toFixed(2)} {currencySymbol}/l</span>
                    </div>
                  );
                }

                // /DARAB calculation
                if (isPiece) {
                  const pricePerPiece = originalPrice / quantity;
                  results.push(
                    <div key="piece" className="flex justify-between text-sm">
                      <span className="text-yellow-300">🔢 Ár/darab:</span>
                      <span className="font-black text-yellow-200">{pricePerPiece.toFixed(2)} {currencySymbol}/db</span>
                    </div>
                  );
                }

                return results.length > 0 ? results : null;
              })()}

              {/* Show different unit prices from group members if they differ - ONLY if API didn't already provide price_per_unit */}
              {product.group_members && product.group_members.length > 1 && (() => {
                // If API already provided price_per_unit in packagings, don't show alternative unit prices
                // (API already gave us the correct unit price based on base_unit)
                const hasApiUnitPrice = product.packagings && product.packagings.some((pkg: any) => pkg.price_per_unit);
                if (hasApiUnitPrice) {
                  return null;
                }

                const currencySymbol = product.currency_symbol || getCurrencySymbol(product.price_currency || 'EUR');
                const unitPrices = product.group_members.map((m: any) => {
                  const quantity = parseFloat(m.variant_quantity || '0') || 0;
                  const rawUnit = (m.variant_unit || '').toLowerCase();
                  const unit = rawUnit.replace(/\s+/g, '');

                  if ((unit === 'g' || unit.includes('gram')) && quantity > 0) {
                    return ((m.original_price || m.price) / quantity * 100).toFixed(2);
                  }
                  return null;
                }).filter(Boolean);

                const uniqueUnitPrices = [...new Set(unitPrices)];
                const mainQuantity = parseFloat(product.variant_quantity || '0') || 0;
                const mainUnit = (product.variant_unit || '').toLowerCase().replace(/\s+/g, '');
                const isMainG = mainUnit === 'g' || mainUnit.includes('gram');
                const mainPrice = isMainG && mainQuantity > 0 ? (originalPrice / mainQuantity * 100).toFixed(2) : null;
                const otherPrices = uniqueUnitPrices.filter(p => p !== mainPrice);

                return otherPrices.length > 0 ? (
                  <div className="mt-1 space-y-1">
                    {otherPrices.map((price, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-yellow-300">💪 Ár/100g:</span>
                        <span className="font-black text-yellow-200">{price} {currencySymbol}/100g</span>
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}

              {/* Discounted Unit Price */}
              {product.discounted_price && product.discounted_price < originalPrice && (
                <div className="border-t border-yellow-500/20 pt-2 mt-2">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-yellow-300 font-black text-sm">📉 Kedvezményes ár:</span>
                    <div className="flex gap-1">
                      <Badge className="text-xs bg-red-500 text-white">
                        -{Math.round(((originalPrice - product.discounted_price) / originalPrice) * 100)}%
                      </Badge>
                      {product.group_members && product.group_members.length > 1 && (() => {
                        const groupOrigPrices = product.group_members.map((m: any) => m.original_price || m.price).filter(Boolean);
                        const uniqueOrigPrices = [...new Set(groupOrigPrices.map((p: any) => p.toFixed(2)))];
                        const mainDiscount = Math.round(((originalPrice - product.discounted_price) / originalPrice) * 100);
                        const otherDiscounts = uniqueOrigPrices.map((price) => {
                          const origPrice = parseFloat(price as string);
                          const member = product.group_members.find((m: any) => (m.original_price || m.price).toFixed(2) === price);
                          const memberDiscountedPrice = member?.discounted_price || member?.original_price || member?.price || origPrice;
                          return origPrice > 0 ? Math.round(((origPrice - memberDiscountedPrice) / origPrice) * 100) : 0;
                        }).filter((d, idx, arr) => arr.indexOf(d) === idx && d !== mainDiscount && d > 0);

                        return otherDiscounts.length > 0 ? (
                          <>
                            {otherDiscounts.map((discount, idx) => (
                              <Badge key={idx} className="text-xs bg-red-500 text-white">
                                -{discount}%
                              </Badge>
                            ))}
                          </>
                        ) : null;
                      })()}
                    </div>
                  </div>
                  {product.min_quantity && product.min_quantity > 1 && (
                    <div className="text-xs text-yellow-300/70 mb-2 font-semibold">
                      (min. {product.min_quantity} db vásárlása esetén)
                    </div>
                  )}
                  {(() => {
                    const currencySymbol = product.currency_symbol || getCurrencySymbol(product.price_currency || 'EUR');
                    const discountedResults: JSX.Element[] = [];

                    // If packagings have price_per_unit info from API, show ALL packagings
                    if (product.packagings && product.packagings.length > 0) {
                      // Get unique packaging sizes to avoid duplicates
                      const uniqueSizeMap = new Map();
                      product.packagings.forEach((pkg: any) => {
                        const sizeKey = `${pkg.size_value}${pkg.size_unit}`;
                        if (!uniqueSizeMap.has(sizeKey)) {
                          uniqueSizeMap.set(sizeKey, pkg);
                        }
                      });

                      uniqueSizeMap.forEach((pkg: any) => {
                        if (pkg.discounted_price_per_unit) {
                          // Skip /100g price if base unit is /kg (to avoid redundancy)
                          const skipThisPrice = pkg.discounted_price_per_unit.unit === '/100g' && pkg.discounted_price_per_unit.label.includes('/kg');
                          if (!skipThisPrice) {
                            discountedResults.push(
                              <div key={`discounted-per-unit-${pkg.size_value}${pkg.size_unit}`} className="flex justify-between text-sm">
                                <span className="text-yellow-300">💪 {pkg.discounted_price_per_unit.label} ({pkg.size_value} {pkg.size_unit}):</span>
                                <span className="font-black text-yellow-200">{parseFloat(pkg.discounted_price_per_unit.value).toFixed(2)} {currencySymbol}{pkg.discounted_price_per_unit.unit}</span>
                              </div>
                            );
                          }
                        }
                      });

                      if (discountedResults.length > 0) {
                        return discountedResults;
                      }
                    }

                    // Fallback: calculate from variant_quantity if API data not available
                    const quantity = parseFloat(product.variant_quantity || product.attributes?.['__custom_variant_quantity']) || 0;
                    const rawUnit = (product.variant_unit || product.attributes?.['__custom_variant_unit_name'] || '').toLowerCase();

                    const unit = rawUnit.replace(/\s+/g, '');
                    const isKg = unit.includes('kg') || unit.includes('kilogram');
                    const isG = unit === 'g' || unit.includes('gram');
                    const isMl = unit.includes('ml') || unit.includes('millil');
                    const isCl = unit.includes('cl');
                    const isDl = unit.includes('dl');
                    const isL = (unit === 'l' || unit.includes('liter') || unit.includes('litre') || unit.includes('ltr')) && !isMl && !isCl && !isDl;
                    const isPiece = unit.includes('darab') || unit.includes('db') || unit.includes('pcs') || unit.includes('piece');

                    // Same calculations but with discounted_price
                    if (isKg || isG) {
                      if (isKg) {
                        const pricePerKg = product.discounted_price / quantity;
                        discountedResults.push(
                          <div key="kg" className="flex justify-between text-sm">
                            <span className="text-yellow-300">💪 Ár/kg:</span>
                            <span className="font-black text-yellow-200">{pricePerKg.toFixed(2)} {currencySymbol}/kg</span>
                          </div>
                        );
                      } else {
                        const pricePer100g = (product.discounted_price / quantity) * 100;
                        discountedResults.push(
                          <div key="100g" className="flex justify-between text-sm">
                            <span className="text-yellow-300">💪 Ár/100g:</span>
                            <span className="font-black text-yellow-200">{pricePer100g.toFixed(2)} {currencySymbol}/100g</span>
                          </div>
                        );
                      }
                    }

                    if (isMl) {
                      const pricePer100ml = (product.discounted_price / quantity) * 100;
                      discountedResults.push(
                        <div key="100ml" className="flex justify-between text-sm">
                          <span className="text-yellow-300">💧 Ár/100ml:</span>
                          <span className="font-black text-yellow-200">{pricePer100ml.toFixed(2)} {currencySymbol}/100ml</span>
                        </div>
                      );
                    } else if (isCl) {
                      const pricePerCl = product.discounted_price / quantity;
                      discountedResults.push(
                        <div key="cl" className="flex justify-between text-sm">
                          <span className="text-yellow-300">💧 Ár/cl:</span>
                          <span className="font-black text-yellow-200">{pricePerCl.toFixed(2)} {currencySymbol}/cl</span>
                        </div>
                      );
                    } else if (isDl) {
                      const pricePerDl = product.discounted_price / quantity;
                      discountedResults.push(
                        <div key="dl" className="flex justify-between text-sm">
                          <span className="text-yellow-300">💧 Ár/dl:</span>
                          <span className="font-black text-yellow-200">{pricePerDl.toFixed(2)} {currencySymbol}/dl</span>
                        </div>
                      );
                    } else if (isL) {
                      const pricePerL = product.discounted_price / quantity;
                      discountedResults.push(
                        <div key="l" className="flex justify-between text-sm">
                          <span className="text-yellow-300">💧 Ár/l:</span>
                          <span className="font-black text-yellow-200">{pricePerL.toFixed(2)} {currencySymbol}/l</span>
                        </div>
                      );
                    }

                    if (isPiece) {
                      const pricePerPiece = product.discounted_price / quantity;
                      discountedResults.push(
                        <div key="piece" className="flex justify-between text-sm">
                          <span className="text-yellow-300">🔢 Ár/darab:</span>
                          <span className="font-black text-yellow-200">{pricePerPiece.toFixed(2)} {currencySymbol}/db</span>
                        </div>
                      );
                    }

                    return discountedResults.length > 0 ? discountedResults : null;
                  })()}

                  {/* REMOVED: Show different discounted unit prices from group members - redundant since API already returns price_per_unit for all packagings */}
                </div>
              )}
            </div>
          )}

          {/* Admin Edit Button */}
          {isAdmin && product.library_id && product.slice_id && (
            <div className="w-full mt-4 pt-4 border-t border-white/5">
              <button
                onClick={() => {
                  navigate(`/admin/annotator/${product.library_id}/${product.slice_id}?page=${currentPage}`);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500/30 to-blue-500/30 hover:from-purple-500/50 hover:to-blue-500/50 border border-purple-500/50 text-purple-200 rounded-lg transition-all font-medium text-sm"
              >
                <Edit2 className="h-4 w-4" />
                Szerkesztés
              </button>
            </div>
          )}
        </CardFooter>
      </Card >
      <ImageModal
        isOpen={isImageModalOpen}
        imageUrl={product.slice_url}
        productName={product.product_name}
        onClose={() => setIsImageModalOpen(false)}
      />
    </>
  );
};
const DiscountProductList = () => {
  const [searchProductName, setSearchProductName] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState<any[]>([]);
  const [displayedProducts, setDisplayedProducts] = useState<any[]>([]);
  const [totalOffers, setTotalOffers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [orderBy, setOrderBy] = useState("name_asc");
  const [hasMorePages, setHasMorePages] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9);
  const [maxPageReached, setMaxPageReached] = useState(false);

  // Collapsible panel states
  const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlSearch = searchParams.get("search") || "";
  const urlCategoryId = searchParams.get("category_id") || "";
  const urlParentCategoryId = searchParams.get("parent_category_id") || "";
  const urlGrandparentCategoryId = searchParams.get("grandparent_category_id") || "";

  // Handle filter changes
  const handleFilterChange = (type: string, id: number | string) => {
    const params = new URLSearchParams();
    if (type === "category") {
      params.set("category_id", String(id));
    } else if (type === "parent_category") {
      params.set("parent_category_id", String(id));
    } else if (type === "grandparent_category") {
      params.set("grandparent_category_id", String(id));
    }
    navigate(`/discounts?${params.toString()}`);
  };

  // Initialize search input from URL parameter on mount
  useEffect(() => {
    if (urlSearch) {
      setSearchProductName(urlSearch);
    }
  }, [urlSearch]);

  // Get category and parent category names from displayed products
  const getCategoryName = (categoryId: string) => {
    const product = displayedProducts.find(p => p.category_id === parseInt(categoryId));
    return product?.category_name;
  };

  const getParentCategoryName = (parentCategoryId: string) => {
    const product = displayedProducts.find(p => p.parent_category_id === parseInt(parentCategoryId));
    return product?.parent_category_name;
  };

  const getGrandparentCategoryName = (grandparentCategoryId: string) => {
    const product = displayedProducts.find(p => p.grandparent_category_id === parseInt(grandparentCategoryId));
    return product?.grandparent_category_name;
  };

  const handleRemoveFilter = (type: string) => {
    const params = new URLSearchParams();
    if (type === "grandparent_category" && urlParentCategoryId) {
      params.set("parent_category_id", urlParentCategoryId);
    } else if (type === "parent_category" && urlCategoryId) {
      params.set("category_id", urlCategoryId);
    } else if (type === "category" && urlParentCategoryId) {
      params.set("parent_category_id", urlParentCategoryId);
    } else if (type === "parent_category" && urlGrandparentCategoryId) {
      params.set("grandparent_category_id", urlGrandparentCategoryId);
    }
    navigate(`/discounts${params.toString() ? "?" + params.toString() : ""}`);
  };

  // Load products when URL search parameter changes
  useEffect(() => {
    const loadProductsFromUrl = async () => {
      setLoading(true);
      // Reset to first page when filters change - de ezt az effect dependency-ben tesszük
      setCurrentPage(1);

      try {
        const params = new URLSearchParams();

        if (urlSearch) {
          params.append('product_name', urlSearch.trim());
        }

        if (urlCategoryId) {
          params.append('category_id', urlCategoryId);
        }

        if (urlParentCategoryId) {
          params.append('parent_category_id', urlParentCategoryId);
        }

        if (urlGrandparentCategoryId) {
          params.append('grandparent_category_id', urlGrandparentCategoryId);
        }

        params.append('order_by', orderBy);
        params.append('page', '1');
        params.append('limit', String(itemsPerPage));

        const response = await fetch(`/api/app/discounts?${params.toString()}`, {
          cache: 'no-cache'
        });
        if (response.ok) {
          const data = await response.json();
          const instances = (data.product_instances || []).reverse();

          setDisplayedProducts(instances);
          setTotalOffers(data.total_offers || 0);
          const hasMore = data.has_more_pages || false;
          setHasMorePages(hasMore);

          // Ha nincs több oldal, akkor elértük a maximumot
          if (!hasMore) {
            setMaxPageReached(true);
          } else {
            setMaxPageReached(false);
          }
        }
      } catch (error) {
        console.error('Error fetching discounts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProductsFromUrl();
  }, [urlSearch, urlCategoryId, urlParentCategoryId, urlGrandparentCategoryId, orderBy]);

  // Search suggestions as user types
  useEffect(() => {
    if (searchProductName.trim().length < 2) {
      setSearchSuggestions([]);
      setShowDropdown(false);
      return;
    }

    // Don't show dropdown if we're just loading from URL
    if (searchProductName === urlSearch) {
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    setShowDropdown(true); // Show dropdown immediately when typing

    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        params.append('product_name', searchProductName.trim());
        params.append('order_by', orderBy);

        const response = await fetch(`/api/app/discounts?${params.toString()}`, {
          cache: 'no-cache'
        });
        if (response.ok) {
          const data = await response.json();
          const instances = data.product_instances || [];

          setSearchSuggestions(instances);
          setShowDropdown(true);
        }
      } catch (error) {
        console.error('Error searching products:', error);
      } finally {
        setIsSearching(false);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [searchProductName, urlSearch]);

  const handleSelectProduct = (productName: string) => {
    setSearchProductName(productName);
    setShowDropdown(false);
    navigate(`/discounts?search=${encodeURIComponent(productName)}`);
  };

  // Load products for specific page
  useEffect(() => {
    const loadProductsFromPage = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();

        if (urlSearch) {
          params.append('product_name', urlSearch.trim());
        }

        if (urlCategoryId) {
          params.append('category_id', urlCategoryId);
        }

        if (urlParentCategoryId) {
          params.append('parent_category_id', urlParentCategoryId);
        }

        if (urlGrandparentCategoryId) {
          params.append('grandparent_category_id', urlGrandparentCategoryId);
        }

        params.append('order_by', orderBy);
        params.append('page', String(currentPage));
        params.append('limit', String(itemsPerPage));

        const response = await fetch(`/api/app/discounts?${params.toString()}`, {
          cache: 'no-cache'
        });
        if (response.ok) {
          const data = await response.json();
          const instances = (data.product_instances || []).reverse();

          setDisplayedProducts(instances);
          setTotalOffers(data.total_offers || 0);
          const hasMore = data.has_more_pages || false;
          setHasMorePages(hasMore);

          // Ha nincs több oldal, akkor elértük a maximumot
          if (!hasMore) {
            setMaxPageReached(true);
          } else {
            // Ha van több oldal, reseteljük a flag-et
            setMaxPageReached(false);
          }
        }
      } catch (error) {
        console.error('Error fetching discounts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProductsFromPage();
  }, [currentPage, itemsPerPage, orderBy, urlSearch, urlCategoryId, urlParentCategoryId, urlGrandparentCategoryId]);

  // Lapozás kalkuláció - SLICE alapú
  // maxPageReached jelzi az utolsó oldalt
  // Mivel az API slice-alapú lapozást használ, az totalPages mindig legalább az aktuális oldal száma
  const totalPages = maxPageReached ? currentPage : currentPage + 1;

  // Lapozási gombok generálása - fejlett logika
  // Mutatja az első 2, közte ..., majd 1-2 balra, aktuális, 1-2 jobbra, majd ..., végül utolsó 2
  const generatePageNumbers = () => {
    const pages: (number | string)[] = [];
    const delta = 1; // 1 oldal az aktuális körül (összesen 3 oldal: -1, 0, +1)

    // Első 2 oldal
    if (totalPages >= 1) pages.push(1);
    if (totalPages >= 2) pages.push(2);

    // Gap az első 2 és a keresési terület között
    const searchLeft = Math.max(1, currentPage - delta);
    if (searchLeft > 2) {
      if (searchLeft > 3) pages.push('...');
      // Ha csak 1 oldal van között, azt is hozzáadjuk
      if (searchLeft === 3) pages.push(3);
    }

    // Keresési terület körül: aktuális +/- 1 oldal
    const searchRight = Math.min(totalPages, currentPage + delta);
    for (let i = searchLeft; i <= searchRight; i++) {
      if (!pages.includes(i)) pages.push(i);
    }

    // Gap a keresési terület és az utolsó 2 között
    if (searchRight < totalPages - 1) {
      if (searchRight < totalPages - 2) pages.push('...');
      // Ha csak 1 oldal van között, azt is hozzáadjuk
      if (searchRight === totalPages - 2) pages.push(totalPages - 1);
    }

    // Utolsó 2 oldal
    if (totalPages > searchRight) {
      if (totalPages > 2 && totalPages - 1 !== searchRight && !pages.includes(totalPages - 1)) {
        pages.push(totalPages - 1);
      }
      if (!pages.includes(totalPages)) pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="space-y-6">
      {/* Search & Filter Panels */}
      <div className="space-y-3">
        {/* Keresés és Rendezés Panel */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm">
          <button
            onClick={() => setIsSearchPanelOpen(!isSearchPanelOpen)}
            className="w-full flex items-center justify-between p-6 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
          >
            <h3 className="font-semibold text-lg text-gray-900 dark:text-white flex items-center gap-2">
              <i className="fa-solid fa-search"></i> Keresés és Rendezés
            </h3>
            <span className="text-gray-500 dark:text-gray-400">
              {isSearchPanelOpen ? '▼' : '▶'}
            </span>
          </button>

          {isSearchPanelOpen && (
            <div className="p-6 border-t border-gray-200 dark:border-slate-700 space-y-4">
              {/* Order By */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">Rendezés:</label>
                <select
                  value={orderBy}
                  onChange={(e) => {
                    setOrderBy(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full sm:w-auto px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="name_asc">Név szerint (A-Z)</option>
                  <option value="name_desc">Név szerint (Z-A)</option>
                  <option value="price_asc">Ár szerint (olcsó-először)</option>
                  <option value="price_desc">Ár szerint (drága-először)</option>
                  <option value="discounted_price_asc">Kedvezményes ár szerint (olcsó-először)</option>
                  <option value="discounted_price_desc">Kedvezményes ár szerint (drága-először)</option>
                  <option value="discount_percent_desc">Kedvezmény % szerint (magas-először)</option>
                  <option value="discount_percent_asc">Kedvezmény % szerint (alacsony-először)</option>
                </select>
              </div>

              {/* Product Name Search */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Termék neve..."
                  value={searchProductName}
                  onChange={(e) => setSearchProductName(e.target.value)}
                  onFocus={() => searchProductName.length >= 2 && setShowDropdown(true)}
                  className="w-full pl-10 pr-8 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {searchProductName && (
                  <button
                    onClick={() => {
                      setSearchProductName("");
                      setSearchSuggestions([]);
                      setShowDropdown(false);
                      navigate("/discounts");
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-10"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}

                {/* Search Dropdown Suggestions */}
                {showDropdown && searchProductName.trim().length >= 2 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-md shadow-lg max-h-80 overflow-y-auto z-20">
                    {isSearching ? (
                      <div className="flex items-center justify-center gap-2 p-4 text-gray-500 dark:text-gray-400">
                        <Loader className="h-4 w-4 animate-spin text-blue-500" />
                        <span className="text-sm">Keresünk...</span>
                      </div>
                    ) : searchSuggestions.length === 0 ? (
                      <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
                        Nincsenek a keresésnek megfelelő termékek.
                      </div>
                    ) : (
                      <div className="space-y-0">
                        {searchSuggestions.map((product) => (
                          <button
                            key={product.id}
                            onClick={() => handleSelectProduct(product.product_name)}
                            className="w-full flex items-center gap-3 p-3 border-b border-gray-100 dark:border-slate-700 hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors text-left"
                          >
                            <img
                              src={product.slice_url}
                              alt={product.product_name}
                              className="h-12 w-12 object-cover rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 dark:text-white truncate">
                                {product.product_name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {product.shop_name || "N/A"} • {product.price_amount} {product.price_currency}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Active Filters Display */}
        {(urlCategoryId || urlParentCategoryId || urlGrandparentCategoryId) && (
          <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-200">Szűrés:</span>
              {urlGrandparentCategoryId && (
                <div className="flex items-center gap-2 bg-white dark:bg-slate-800 px-3 py-1 rounded-full border border-blue-200 dark:border-blue-700">
                  <span className="text-sm text-gray-900 dark:text-white">
                    📦 {getGrandparentCategoryName(urlGrandparentCategoryId)}
                  </span>
                  <button
                    onClick={() => handleRemoveFilter("grandparent_category")}
                    className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
              {urlParentCategoryId && (
                <div className="flex items-center gap-2 bg-white dark:bg-slate-800 px-3 py-1 rounded-full border border-blue-200 dark:border-blue-700">
                  <span className="text-sm text-gray-900 dark:text-white">
                    📁 {getParentCategoryName(urlParentCategoryId)}
                  </span>
                  <button
                    onClick={() => handleRemoveFilter("parent_category")}
                    className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
              {urlCategoryId && (
                <div className="flex items-center gap-2 bg-white dark:bg-slate-800 px-3 py-1 rounded-full border border-blue-200 dark:border-blue-700">
                  <span className="text-sm text-gray-900 dark:text-white">
                    📂 {getCategoryName(urlCategoryId)}
                  </span>
                  <button
                    onClick={() => handleRemoveFilter("category")}
                    className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-12 text-white/60 font-semibold flex items-center justify-center gap-2">
            <Loader className="h-5 w-5 animate-spin" />
            Termékelőfordulások betöltése...
          </div>
        ) : displayedProducts.length === 0 ? (
          <div className="text-center py-12 glass-panel border border-white/10 rounded-3xl">
            <i className="fa-solid fa-box-open text-5xl text-white/40 mb-4 block"></i>
            <p className="text-white/60 font-semibold">Nincsenek felvett termékelőfordulások.</p>
          </div>
        ) : (
          <>
            {/* Per Page Selector - Képek alapú paginálás */}
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-white">Ajánlatok oldalanként:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1 bg-slate-800 border border-slate-600 text-white rounded text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value={9}>9 kép</option>
                  <option value={18}>18 kép</option>
                  <option value={27}>27 kép</option>
                  <option value={36}>36 kép</option>
                </select>
              </div>
              <div className="text-sm text-white/60">
                {totalOffers} összes kép ({itemsPerPage} kép oldalanként)
              </div>
            </div>

            {/* Top Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 p-4 bg-white/5 rounded-lg border border-white/10 flex-wrap">
                {/* First Page */}
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-slate-800 border border-slate-600 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  &lt;&lt;
                </button>

                {/* Previous 2 */}
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 2))}
                  disabled={currentPage <= 2}
                  className="px-3 py-1 bg-slate-800 border border-slate-600 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  ← 2
                </button>

                {/* Previous */}
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-slate-800 border border-slate-600 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  &lt;
                </button>

                {/* Page Numbers */}
                <div className="flex gap-1">
                  {generatePageNumbers().map((page, idx) => (
                    page === '...' ? (
                      <span key={idx} className="px-2 py-1 text-white/60">…</span>
                    ) : (
                      <button
                        key={idx}
                        onClick={() => setCurrentPage(page as number)}
                        className={`px-3 py-1 rounded text-sm transition-colors ${currentPage === page
                          ? 'bg-blue-600 text-white border border-blue-500'
                          : 'bg-slate-800 border border-slate-600 text-white hover:bg-slate-700'
                          }`}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>

                {/* Next */}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={!hasMorePages}
                  className="px-3 py-1 bg-slate-800 border border-slate-600 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  &gt;
                </button>

                {/* Next 2 */}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 2))}
                  disabled={!hasMorePages}
                  className="px-3 py-1 bg-slate-800 border border-slate-600 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  2 →
                </button>

                {/* Last Page */}
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={!hasMorePages}
                  className="px-3 py-1 bg-slate-800 border border-slate-600 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  &gt;&gt;
                </button>
              </div>
            )}

            {/* Products Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayedProducts.map((product) => {
                return (
                  <ProductCardV1
                    key={product.id}
                    product={product}
                    onFilterChange={handleFilterChange}
                    currentPage={currentPage}
                  />
                );
              })}
            </div>

            {/* Bottom Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 p-4 bg-white/5 rounded-lg border border-white/10 flex-wrap">
                {/* First Page */}
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-slate-800 border border-slate-600 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  &lt;&lt;
                </button>

                {/* Previous 2 */}
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 2))}
                  disabled={currentPage <= 2}
                  className="px-3 py-1 bg-slate-800 border border-slate-600 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  ← 2
                </button>

                {/* Previous */}
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-slate-800 border border-slate-600 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  &lt;
                </button>

                {/* Page Numbers */}
                <div className="flex gap-1">
                  {generatePageNumbers().map((page, idx) => (
                    page === '...' ? (
                      <span key={idx} className="px-2 py-1 text-white/60">…</span>
                    ) : (
                      <button
                        key={idx}
                        onClick={() => setCurrentPage(page as number)}
                        className={`px-3 py-1 rounded text-sm transition-colors ${currentPage === page
                          ? 'bg-blue-600 text-white border border-blue-500'
                          : 'bg-slate-800 border border-slate-600 text-white hover:bg-slate-700'
                          }`}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>

                {/* Next */}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={!hasMorePages}
                  className="px-3 py-1 bg-slate-800 border border-slate-600 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  &gt;
                </button>

                {/* Next 2 */}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 2))}
                  disabled={!hasMorePages}
                  className="px-3 py-1 bg-slate-800 border border-slate-600 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  2 →
                </button>

                {/* Last Page */}
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={!hasMorePages}
                  className="px-3 py-1 bg-slate-800 border border-slate-600 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  &gt;&gt;
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DiscountProductList;
